import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Trophy, Zap, TrendingUp, Globe, Tag, User, Loader2, CheckCircle2, Rocket, Target, Users } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Country emoji options
const COUNTRIES = [
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'IN', name: 'India', flag: '🇮🇳' },
  { code: 'CN', name: 'China', flag: '🇨🇳' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
  { code: 'ZA', name: 'South Africa', flag: '🇿🇦' },
  { code: 'EG', name: 'Egypt', flag: '🇪🇬' },
  { code: 'NG', name: 'Nigeria', flag: '🇳🇬' },
  { code: 'KE', name: 'Kenya', flag: '🇰🇪' },
  { code: 'SG', name: 'Singapore', flag: '🇸🇬' },
  { code: 'AE', name: 'UAE', flag: '🇦🇪' },
  { code: 'IL', name: 'Israel', flag: '🇮🇱' },
  { code: 'SE', name: 'Sweden', flag: '🇸🇪' }
];

const SubmissionModal = ({ 
  open, 
  onClose, 
  challengeId, 
  sessionId,
  oiScore, 
  msaScore,
  ideaTitle,
  ideaDescription 
}) => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [impactLabCreating, setImpactLabCreating] = useState(false);
  const [impactLabId, setImpactLabId] = useState(null);
  const [formData, setFormData] = useState({
    author: '',
    country: '',
    summary: ideaDescription || '',
    impact: '',
    tags: ''
  });

  const calculateOILabel = (score) => {
    if (score >= 9) return 'Exceptional';
    if (score >= 8) return 'Very High';
    if (score >= 6) return 'Moderate';
    return 'Low Originality';
  };

  const calculateSparkLabel = (sparkPct) => {
    if (sparkPct >= 80) return 'Exceptional Spark';
    if (sparkPct >= 60) return 'Strong Spark';
    if (sparkPct >= 40) return 'Hybrid Originality';
    if (sparkPct >= 20) return 'Safe/Conventional';
    return 'Derivative';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.author.trim() || !formData.country || !formData.summary.trim() || !formData.impact.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    setSubmitting(true);

    try {
      const selectedCountry = COUNTRIES.find(c => c.code === formData.country);
      const tagsArray = formData.tags.split(',').map(t => t.trim()).filter(t => t);

      const submission = {
        challenge_id: challengeId,
        session_id: sessionId,
        title: ideaTitle,
        author: formData.author,
        country: selectedCountry?.flag || '🌍',
        summary: formData.summary,
        impact: formData.impact,
        tags: tagsArray,
        oi_score: oiScore.total,
        oi_label: calculateOILabel(oiScore.total),
        spark_pct: msaScore.sparkPct,
        spark_label: calculateSparkLabel(msaScore.sparkPct),
        is_public: true
      };

      await axios.post(`${BACKEND_URL}/api/challenges/${challengeId}/submissions`, submission, {
        withCredentials: true
      });
      
      setSuccess(true);
      
      // Auto-create Impact Lab after successful submission
      setImpactLabCreating(true);
      try {
        const labResponse = await axios.post(
          `${BACKEND_URL}/api/impact-lab/create?challenge_id=${challengeId}`,
          {},
          { withCredentials: true }
        );
        
        if (labResponse.data.lab_id) {
          setImpactLabId(labResponse.data.lab_id);
        }
      } catch (labError) {
        console.error('Impact Lab creation error:', labError);
        // Don't block on Impact Lab creation failure
      } finally {
        setImpactLabCreating(false);
      }
    } catch (error) {
      console.error('Error submitting:', error);
      alert('Failed to submit. Please try again.');
      setSubmitting(false);
    }
  };

  const handleGoToImpactLab = () => {
    if (impactLabId) {
      navigate(`/impact-lab/${impactLabId}`);
    } else {
      navigate(`/impact-lab-analysis?challenge=${challengeId}`);
    }
    onClose();
  };

  const handleViewSubmissions = () => {
    console.log('Navigating to challenge with ID:', challengeId);
    onClose();
    navigate(`/challenge/${challengeId}`);
  };

  if (success) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-lg">
          <div className="text-center py-6">
            <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full mx-auto mb-4 flex items-center justify-center animate-bounce">
              <CheckCircle2 className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">🎉 Submission Successful!</h3>
            <p className="text-slate-600 mb-6">Your solution has been published to the challenge gallery.</p>
            
            {/* Impact Lab Auto-Creation Section */}
            <div className="bg-gradient-to-r from-orange-50 to-red-50 border-2 border-orange-200 rounded-xl p-6 mb-6">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Target className="w-6 h-6 text-orange-600" />
                <h4 className="font-bold text-lg text-orange-900">Impact Lab Ready!</h4>
              </div>
              
              {impactLabCreating ? (
                <div className="flex items-center justify-center gap-2 text-orange-700">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Creating your Impact Lab...</span>
                </div>
              ) : (
                <>
                  <p className="text-sm text-orange-800 mb-4">
                    Turn your idea into reality with a <strong>7-day IDEO Design Sprint</strong>. 
                    Invite collaborators and bring your solution to life!
                  </p>
                  
                  <div className="grid grid-cols-3 gap-2 mb-4 text-xs">
                    <div className="bg-white rounded-lg p-2 border border-orange-200">
                      <Rocket className="w-4 h-4 mx-auto text-orange-600 mb-1" />
                      <span className="text-orange-800">7-Day Sprint</span>
                    </div>
                    <div className="bg-white rounded-lg p-2 border border-orange-200">
                      <Users className="w-4 h-4 mx-auto text-orange-600 mb-1" />
                      <span className="text-orange-800">Team Up</span>
                    </div>
                    <div className="bg-white rounded-lg p-2 border border-orange-200">
                      <Trophy className="w-4 h-4 mx-auto text-orange-600 mb-1" />
                      <span className="text-orange-800">Build MVP</span>
                    </div>
                  </div>
                  
                  <Button
                    onClick={handleGoToImpactLab}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold"
                  >
                    <Target className="w-4 h-4 mr-2" />
                    Start Impact Lab Now
                  </Button>
                </>
              )}
            </div>
            
            <Button
              variant="outline"
              onClick={handleViewSubmissions}
              className="w-full"
            >
              View All Submissions
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">Submit Your Solution</DialogTitle>
          <DialogDescription>
            Publish your idea to the global challenge gallery
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* OI and Spark Scores - Prominently Displayed */}
          <div className="grid md:grid-cols-2 gap-4 p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border-2 border-purple-200">
            <div className="text-center">
              <Trophy className="w-8 h-8 mx-auto text-purple-600 mb-2" />
              <div className="text-sm text-slate-600 mb-1">Originality Index</div>
              <div className="text-4xl font-bold text-purple-700">{oiScore.total.toFixed(1)}</div>
              <Badge className="mt-2 bg-purple-200 text-purple-800 border-0">
                {calculateOILabel(oiScore.total)}
              </Badge>
              <div className="mt-2 text-xs text-slate-500 space-y-1">
                <div>Novelty: {oiScore.novelty}/10</div>
                <div>Resonance: {oiScore.resonance}/10</div>
                <div>Synthesis: {oiScore.synthesis}/10</div>
                <div>Ethics: {oiScore.ethical}/10</div>
              </div>
            </div>
            
            <div className="text-center">
              <Zap className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
              <div className="text-sm text-slate-600 mb-1">Spark Level</div>
              <div className="text-4xl font-bold text-emerald-700">{msaScore.sparkPct.toFixed(0)}%</div>
              <Badge className="mt-2 bg-emerald-200 text-emerald-800 border-0">
                {calculateSparkLabel(msaScore.sparkPct)}
              </Badge>
              <div className="mt-2 text-xs text-slate-500">
                Mirror: {msaScore.mirrorPct.toFixed(0)}% | Spark: {msaScore.sparkPct.toFixed(0)}%
              </div>
            </div>
          </div>

          {/* Idea Title (read-only) */}
          <div>
            <Label className="text-sm font-semibold">Solution Title</Label>
            <div className="mt-1 p-3 bg-slate-50 rounded-md border">
              <p className="font-medium text-slate-900">{ideaTitle}</p>
            </div>
          </div>

          {/* Author Name */}
          <div>
            <Label htmlFor="author" className="text-sm font-semibold flex items-center gap-2">
              <User className="w-4 h-4" />
              Your Name *
            </Label>
            <Input
              id="author"
              value={formData.author}
              onChange={(e) => setFormData({ ...formData, author: e.target.value })}
              placeholder="Enter your name"
              className="mt-1"
              required
            />
          </div>

          {/* Country Selector */}
          <div>
            <Label htmlFor="country" className="text-sm font-semibold flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Country *
            </Label>
            <select
              id="country"
              value={formData.country}
              onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              required
            >
              <option value="">Select your country</option>
              {COUNTRIES.map(country => (
                <option key={country.code} value={country.code}>
                  {country.flag} {country.name}
                </option>
              ))}
            </select>
          </div>

          {/* Summary/Abstract */}
          <div>
            <Label htmlFor="summary" className="text-sm font-semibold">
              Solution Summary *
            </Label>
            <p className="text-xs text-slate-500 mt-1">Brief description of your solution (2-3 sentences)</p>
            <Textarea
              id="summary"
              value={formData.summary}
              onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
              placeholder="Describe your solution clearly and concisely..."
              className="mt-2 min-h-[100px]"
              required
            />
          </div>

          {/* Impact Statement */}
          <div>
            <Label htmlFor="impact" className="text-sm font-semibold flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Projected Impact *
            </Label>
            <p className="text-xs text-slate-500 mt-1">What measurable impact will this solution have?</p>
            <Textarea
              id="impact"
              value={formData.impact}
              onChange={(e) => setFormData({ ...formData, impact: e.target.value })}
              placeholder="e.g., Could reduce carbon emissions by 30% in urban areas..."
              className="mt-2 min-h-[80px]"
              required
            />
          </div>

          {/* Tags */}
          <div>
            <Label htmlFor="tags" className="text-sm font-semibold flex items-center gap-2">
              <Tag className="w-4 h-4" />
              Tags (optional)
            </Label>
            <p className="text-xs text-slate-500 mt-1">Add relevant tags, separated by commas</p>
            <Input
              id="tags"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              placeholder="e.g., AI, Renewable Energy, Community"
              className="mt-2"
            />
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Publishing...
                </>
              ) : (
                <>
                  <Trophy className="w-4 h-4 mr-2" />
                  Publish to Gallery
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SubmissionModal;
